import { useEffect, useRef, useState } from "react";

// Демо-виджеты сценариев первого экрана. Данные примерные, тексты рекомендаций
// повторяют те, что строит доска WB (server/controllers/wb/newWbRecommendation.js).

const nf = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 });
const money = (value) =>
  new Intl.NumberFormat("ru-RU", { style: "currency", currency: "RUB", maximumFractionDigits: 0 }).format(Number(value || 0));
const signed = (value) => (value > 0 ? "+" : "") + money(value);

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export function TrialCta({ place, children, onTrial, style }) {
  return (
    <>
      <button className="cta" type="button" style={style} onClick={() => onTrial(place)}>
        {children} <span className="arr" aria-hidden="true">→</span>
      </button>
      <div className="cta-note">
        <span>7 дней бесплатно</span> · карта не нужна
      </div>
    </>
  );
}

const REC_COLUMNS = { today: "Сделать сегодня", week: "На этой неделе", watch: "Наблюдать" };
const RECS = {
  today: [
    ["Цена и маржа", "Высокая", "Худи оверсайз", "Повысить цену до плановой маржи", "Повысить цену до 2 690 ₽", "Маржа 7% ниже плана 20%.", "effect", "≈ +8 700 ₽", 5],
    ["Остатки", "Высокая", "Шапка бини", "Срочно пополнить остаток", "Пополнить WB на 120 шт.: Приволжский — 70 шт., Центральный — 50 шт.", "Товар закончился или находится на низком остатке, но ранее были продажи.", "goal", "Вернуть наличие на 30 дней", 10],
  ],
  week: [
    ["Выкуп и возвраты", "Средняя", "Футболка базовая", "Проверить причины возвратов", "Проверить причины возвратов", "Возвраты выше нормы или растут.", "goal", "Снизить потери на логистике", 10],
    ["Карточка и конверсия", "Средняя", "Носки, 5 пар", "Клики есть, заказов нет", "Проверить цену, карточку и условия доставки", "Клики есть, но заказов нет.", "goal", "Найти причину потери покупателей", 12],
    ["Остатки", "Средняя", "Свитшот базовый", "Проверить зависание остатка", "Не пополнять товар и проверить акцию, карточку или оффер", "Остаток высокий, спрос слабый или снижается.", "goal", "Снизить риск зависания", 8],
  ],
  watch: [
    ["Наблюдать", "Наблюдать", "Лонгслив", "Наблюдать без активного действия", "Повторить проверку через 7 дней", "Активных сигналов для поставки, цены, рекламы, выкупа или возвратов нет.", "goal", "Контроль через 7 дней", 1],
  ],
};
const PRIORITY_CLASS = { Высокая: "p-hi", Средняя: "p-mid" };

function RecCard({ rec }) {
  const [group, priority, item, title, todo, why, resultKind, resultValue, minutes] = rec;
  const [taskCreated, setTaskCreated] = useState(false);
  const [done, setDone] = useState(false);

  return (
    <article className={`rec ${done ? "done" : ""}`} tabIndex={0}>
      <div className="rec-top">
        <span className="rtype">{group}</span>
        <span className={`rprio ${PRIORITY_CLASS[priority] || "p-low"}`}>{priority}</span>
        <span className="ritem">{item}</span>
      </div>
      <b className="ract">{title}</b>
      <p className="rdo"><span>Что сделать:</span> {todo}</p>
      <p className="rwhy"><span>Почему:</span> {why}</p>
      <div className="rfoot">
        {resultKind === "goal" ? (
          <span className="rgoal">Цель: {resultValue}</span>
        ) : (
          <span className="reff">{resultValue} за 7 дней</span>
        )}
        <span className="rmin">≈ {minutes} мин</span>
        <span className="rbtns">
          <button type="button" className="rtask" disabled={taskCreated} onClick={() => setTaskCreated(true)}>
            {taskCreated ? "Задача создана ✓" : "Создать задачу"}
          </button>
          <button type="button" className="rdone" disabled={done} onClick={() => setDone(true)}>
            {done ? "Готово ✓" : "Готово"}
          </button>
        </span>
      </div>
    </article>
  );
}

export function RecsWidget({ onTrial }) {
  const [column, setColumn] = useState("today");

  return (
    <>
      <div className="wlabel">
        <b>Пример: AI-рекомендации Wildberries</b>
        <span className="seg seg-sm rec-tabs" role="group" aria-label="Когда сделать">
          {Object.entries(REC_COLUMNS).map(([key, label]) => (
            <button key={key} type="button" aria-pressed={key === column} onClick={() => setColumn(key)}>
              {label} {RECS[key].length}
            </button>
          ))}
        </span>
      </div>
      <div className="rsum">
        <div><small>Потенциал к прибыли за 7 дней</small><b className="g">≈ +11 300 ₽</b></div>
        <div><small>Риск по остаткам</small><b>1 SKU</b></div>
      </div>
      <div className="demo">
        <div className="rows">
          {RECS[column].map((rec) => <RecCard key={`${column}-${rec[2]}-${rec[3]}`} rec={rec} />)}
        </div>
        <TrialCta place="recs" onTrial={onTrial}>Получить рекомендации для своего магазина</TrialCta>
      </div>
    </>
  );
}

const PNL = [
  ["Выручка", 1240000, "rev"],
  ["Комиссия маркетплейса", -272800],
  ["Логистика", -148800],
  ["Хранение", -18600],
  ["Реклама", -99200],
  ["Себестоимость", -434000],
  ["Налог УСН 6%", -74400],
  ["Чистая прибыль", 192200, "net"],
];
const PNL_REVENUE = 1240000;
const UNIT_ROWS = [
  ["Худи оверсайз", 312, 2490, 186],
  ["Шапка бини", 418, 990, 92],
  ["Носки, 5 пар", 760, 590, 11],
  ["Футболка базовая", 1040, 790, -34],
];

export function MoneyWidget({ onTrial, onScenario }) {
  const [tab, setTab] = useState("pnl");

  return (
    <>
      <div className="wlabel">
        <b>Пример магазина · сентябрь</b>
        <span className="seg seg-sm" role="group" aria-label="Отчёт">
          <button type="button" aria-pressed={tab === "pnl"} onClick={() => setTab("pnl")}>P&amp;L</button>
          <button type="button" aria-pressed={tab === "unit"} onClick={() => setTab("unit")}>Юнит по товарам</button>
        </span>
      </div>
      <div className="demo">
        {tab === "pnl" ? (
          <>
            <div className="pnl">
              {PNL.map(([name, value, kind]) => (
                <div
                  key={name}
                  className={`pl ${kind || ""}`}
                  title={`${Math.round((Math.abs(value) / PNL_REVENUE) * 100)}% выручки`}
                >
                  <span>{name}</span>
                  <i style={{ "--w": `${(Math.abs(value) / PNL_REVENUE) * 100}%` }} />
                  <b className={kind === "net" ? "g" : ""}>
                    {value < 0 ? "−" : ""}{nf.format(Math.abs(value))} ₽
                  </b>
                </div>
              ))}
            </div>
            <p className="fine">Маржа 15,5%. Наведите на строку, чтобы увидеть долю от выручки.</p>
          </>
        ) : (
          <>
            <div className="rows">
              {UNIT_ROWS.map(([name, sales, price, unit]) => (
                <div key={name} className={`prow ${unit < 0 ? "bad" : ""}`}>
                  <span className="nm">{name}</span>
                  <span className="meta">
                    {nf.format(sales)} продаж · цена {nf.format(price)} ₽ · маржа {String(Math.round((unit / price) * 100)).replace("-", "−")}%
                  </span>
                  <span className={`val ${unit < 0 ? "r" : "g"}`}>{signed(unit)}<small>с продажи</small></span>
                </div>
              ))}
            </div>
            <div className="tip">
              <small>Что дальше</small>
              Футболка базовая продаётся в минус. В AI-рекомендациях по ней появится карточка «Цена и маржа».{" "}
              <button type="button" className="linkbtn" onClick={() => onScenario("recs", "money")}>
                Открыть рекомендации <i>→</i>
              </button>
            </div>
          </>
        )}
      </div>
      <TrialCta place="money" onTrial={onTrial} style={{ marginTop: 12 }}>Посчитать мой магазин</TrialCta>
    </>
  );
}

const REVIEW_DRAFT = "Анна, спасибо за отзыв! Рады, что ткань понравилась. Модель действительно маломерит, поэтому советуем брать на размер больше.";

export function ReviewsWidget({ onTrial }) {
  const [typed, setTyped] = useState(0);
  const [published, setPublished] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const generate = () => {
    clearInterval(timerRef.current);
    setPublished(false);
    if (prefersReducedMotion()) {
      setTyped(REVIEW_DRAFT.length);
      return;
    }
    let position = 0;
    timerRef.current = setInterval(() => {
      position = Math.min(REVIEW_DRAFT.length, position + 3);
      setTyped(position);
      if (position >= REVIEW_DRAFT.length) clearInterval(timerRef.current);
    }, 18);
  };

  const ready = typed >= REVIEW_DRAFT.length;

  return (
    <>
      <div className="wlabel"><b>Пример: новые отзывы</b><span>2 без ответа</span></div>
      <div className="demo">
        <div className="review">
          <div className="who"><span>Анна · Худи оверсайз</span><span className="stars" aria-label="4 из 5">★★★★☆</span></div>
          Хорошая ткань, но размер маломерит, пришлось менять.
        </div>
        <div className="draft">
          <small>Ответ</small>
          {typed ? (
            <span>
              {REVIEW_DRAFT.slice(0, typed)}
              {!ready ? <span className="caret" /> : null}
            </span>
          ) : (
            <span style={{ color: "var(--muted)" }}>Нажмите «Сгенерировать»</span>
          )}
        </div>
        <div className="actions">
          <button type="button" className="btn primary" onClick={generate}>Сгенерировать</button>
          <button type="button" className="btn" disabled={!ready || published} onClick={() => setPublished(true)}>
            {published ? "Опубликовано ✓" : "Опубликовать"}
          </button>
        </div>
        <div className="review">
          <div className="who"><span>Игорь · Футболка базовая</span><span className="stars" aria-label="1 из 5">★☆☆☆☆</span></div>
          Пришла с дыркой на рукаве.<br />
          <span className="task">Критический</span>
        </div>
        <TrialCta place="reviews" onTrial={onTrial}>Подключить отзывы</TrialCta>
      </div>
    </>
  );
}

const STOCK_ROWS = [
  ["Шапка бини", 3, "Остаток 12 шт. · FBO", "var(--loss)"],
  ["Худи оверсайз", 6, "Остаток 40 шт. · FBO", "#f08a3c"],
  ["Футболка базовая", 24, "Остаток 190 шт. · FBO", "var(--gain)"],
];

export function StockWidget({ onTrial }) {
  const [supplyReady, setSupplyReady] = useState(false);

  return (
    <>
      <div className="wlabel"><b>Пример: остатки на сегодня</b></div>
      <div className="demo">
        <div className="rows">
          {STOCK_ROWS.map(([name, days, text, color]) => (
            <div key={name} className="prow" tabIndex={0}>
              <span className="nm">{name}</span>
              <span className="meta">{text}</span>
              <span className="val">{days}<small>дн. хватит</small></span>
              <div className="days"><span style={{ width: `${Math.min(100, (days / 30) * 100)}%`, background: color }} /></div>
            </div>
          ))}
        </div>
        <div className="actions">
          <button type="button" className="btn primary" onClick={() => setSupplyReady(true)}>
            {supplyReady ? "Файл поставки готов, задача создана ✓" : "Создать задачу на поставку"}
          </button>
        </div>
        <TrialCta place="stock" onTrial={onTrial}>Проверить мои остатки</TrialCta>
      </div>
    </>
  );
}

const CARD_ATTRS = [
  ["Название", "Худи женское оверсайз из хлопка с капюшоном"],
  ["Категория", "Одежда · Толстовки и худи"],
  ["Состав", "Хлопок 80%, полиэстер 20%"],
  ["Сезон", "Демисезон"],
  ["Покрой", "Оверсайз"],
];

export function CardsWidget({ onTrial }) {
  const [name, setName] = useState("Худи женское хлопок оверсайз");
  const [filled, setFilled] = useState(false);

  return (
    <>
      <div className="wlabel"><b>Пример: новая карточка</b></div>
      <div className="demo">
        <div className="cardin">
          <input value={name} onChange={(event) => setName(event.target.value)} aria-label="Что за товар" />
          <button type="button" className="btn primary" onClick={() => setFilled(true)}>Заполнить</button>
        </div>
        {filled ? (
          <dl className="attrs">
            {CARD_ATTRS.map(([term, value], index) => (
              <div key={term} style={{ display: "contents" }}>
                <dt>{term}</dt>
                <dd style={{ animationDelay: `${index * 0.08}s` }}>{value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        <TrialCta place="cards" onTrial={onTrial}>Заполнить свою карточку</TrialCta>
      </div>
    </>
  );
}
